import { useOnClickOutside } from '@/hooks';
import useScrollIntoView from '@/hooks/useScrollIntoView';
import DropdownButton from '@/storybook/components/FormCollection/components/DropdownButton/DropdownButton';
import DropdownLabel from '@/storybook/components/FormCollection/components/DropdownLabel/DropdownLabel';
import DropdownList from '@/storybook/components/FormCollection/components/DropdownList/DropdownList';
import { DropdownPosition, handleDropdownListPosition } from '@/storybook/components/FormCollection/utils/utils';
import cx from 'classnames';
import { useCallback, useState } from 'react';
import styles from '../Input.module.css';
import { useOldTreeSelect } from './hooks/useOldTreeSelect';
import { TreeSelectProps } from './TreeSelect.types';

export default function OldTreeSelect<T extends string>({
  value,
  onChange,
  options,
  isLoading,
  placeholder,
  searchable,
  disabled,
  label,
  tooltip,
  alignment = 'vertical',
  adobeEventTrackFunctions,
  onDropdownToggle,
  'data-testid': dataTestId,
  dropdownLabelTestId,
  dropdownButtonTestId,
  dropdownListTestId,
  dropdownPosition,
  className,
  collapseSingleChild,
  selectionMode = 'standard',
  nestedKeySort = 'alphabetical',
  actionBarProps,
}: TreeSelectProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownListPosition, setDropdownLitPosition] = useState<DropdownPosition>(dropdownPosition ?? 'down');

  const dropdownRef = useOnClickOutside(() => {
    if (isOpen) onDropdownToggle?.(false);
    setIsOpen(false);
  });

  useScrollIntoView({ elementRef: dropdownRef, isVisible: isOpen });

  const {
    fixedOptions,
    sortedNestedOptions,
    valueLabelList,
    searchInput,
    setSearchInput,
    searchResults,
    setUserOpenedBranches,
    renderNestedOptions,
  } = useOldTreeSelect<T>({
    options,
    value,
    onChange,
    collapseSingleChild,
    selectionMode,
    nestedKeySort,
    disabled,
  });

  const handleToggleDropdown = useCallback(() => {
    if (disabled) return;
    const newState = !isOpen;
    setIsOpen(newState);
    onDropdownToggle?.(newState);
    if (newState) {
      handleDropdownListPosition(dropdownPosition, dropdownRef, setDropdownLitPosition);
      setSearchInput('');
      setUserOpenedBranches(new Set());
    }
  }, [disabled, isOpen, dropdownPosition, dropdownRef, onDropdownToggle, setSearchInput, setUserOpenedBranches]);

  const handleOnClearValue = useCallback(() => {
    if (!disabled) onChange([]);
  }, [disabled, onChange]);

  return (
    <div
      ref={dropdownRef}
      className={cx(styles.inputContainer, className)}
      data-alignment={alignment}
      data-testid={dataTestId}
      onClick={adobeEventTrackFunctions}
    >
      <DropdownLabel label={label} tooltip={tooltip} data-testid={dropdownLabelTestId} />
      <section className={styles.dropdownContainer}>
        <DropdownButton
          type="multiSelect"
          placeholder={placeholder}
          numOfValue={value?.length}
          active={isOpen}
          onClick={handleToggleDropdown}
          onClear={handleOnClearValue}
          clearable={true}
          disabled={disabled}
          data-testid={dropdownButtonTestId}
        >
          {valueLabelList.join(', ')}
        </DropdownButton>
        <DropdownList
          isOpen={isOpen}
          isLoading={isLoading}
          disabled={!!disabled}
          position={dropdownListPosition}
          isSearchable={searchable}
          searchValue={searchInput}
          onSearchValueChange={setSearchInput}
          numberOfSearchResults={searchInput ? searchResults.size : fixedOptions.length}
          data-testid={dropdownListTestId}
          optionsCount={options.length}
          actionBarProps={actionBarProps}
        >
          <div className={styles.treeSelectList}>{renderNestedOptions(sortedNestedOptions)}</div>
        </DropdownList>
      </section>
    </div>
  );
}
